<template>
  <!-- Work Connection Eligibility Case -->
  <div id="work-connection">
    <!-- Relation -->
    <div class="form-section">
      <FormField
        labelText="I/my partner worked for"
        id="eligibility"
        :error="v$.formResponse.connectionType.$error && v$.formResponse.connectionType.$dirty"
        :errorMessage="'This information is required. Please make a selection.'"
      >
        <ul class="radio-group large">
          <li v-for="option in referenceData.eligibilityWorkedForOptions" :key="option.display">
            <input
              type="radio"
              :id="option.returnValue"
              :value="option.display"
              name="eligibilityWorkedFor"
              v-model="v$.formResponse.connectionType.$model"
              :disabled="disableEligibilityFields"
              @change="updateFormFields"
            />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>
    <div v-if="formResponse.connectionType !== ''">
      <!-- Department/Company Name -->
      <div class="form-section" :key="`workplace-name${disableEligibilityFields ? '-disable' : ''}`">
        <template v-if="disableEligibilityFields">
          <FormField id="workplace-name" :labelText="formLabels.connectionInfo.connectionNameLabel">
            <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.connectionName) }}</p>
          </FormField>
        </template>
        <template v-else>
          <FormField
            id="workplace-name"
            :labelText="formLabels.connectionInfo.connectionNameLabel"
            :placeholder="formLabels.connectionInfo.connectionNamePlaceholder"
            :class="validationStatus(v$.formResponse.connectionName)"
            type="text"
            v-model="v$.formResponse.connectionName.$model"
            :error="v$.formResponse.connectionName.$error"
            :errorMessage="workPlaceError"
          />
        </template>
      </div>
      <!-- Person in Department/Company Name -->
      <div class="form-section" :key="`service-connection-name${disableEligibilityFields ? '-disable' : ''}`">
        <template v-if="disableEligibilityFields">
          <FormField id="service-connection-name" labelText="Name of person with work connection">
            <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.relationName) }}</p>
          </FormField>
        </template>
        <template v-else>
          <FormField
            id="service-connection-name"
            labelText="Name of person with work connection"
            placeholder="e.g. George Washington"
            type="text"
            :class="validationStatus(v$.formResponse.relationName)"
            v-model="v$.formResponse.relationName.$model"
            :error="v$.formResponse.relationName.$error"
            :errorMessage="relationNameError"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { required, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { FormField } from '@/components';
import { Eligibility } from '@general/js/enums';
import { getEligibilityWorkLabel, getEligibilityWorkPlaceHolder } from '@general/js/utils';
import { mapMutations } from 'vuex';
import { returnWhiteSpaceIfEmptyStr } from '@/utils';

const NAME_SYMBOL_MESSAGE = "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
const MAX_CHARS_MESSAGE = 'You have exceeded the maximum amount of characters.';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'WorkConnection',

  data() {
    return {
      formLabels: {
        connectionInfo: {
          connectionNameLabel: '',
          connectionNamePlaceholder: '',
        },
      },
      formResponse: { relationName: '', connectionName: '', connectionType: '' },
      eligibility: Eligibility.WORK_CONNECTION,
    };
  },
  components: {
    FormField,
  },

  props: {
    subFormData: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    noEligibilityChange: {
      type: Boolean,
    },
    disableEligibilityFields: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    serviceConnection: {
      type: String,
    },
    defaultSubFormTemplate: {
      type: Object,
    },
    isChangeCover: {
      type: Boolean,
    },
  },

  watch: {
    formResponse: {
      handler() {
        if (this.formResponse) {
          this.$emit('updated', { subFormData: this.formResponse, isInvalid: this.v$.$invalid || this.v$.$error });
        }
      },
      deep: true,
    },
    // Clears and restores prefill data when service connection changes (Self, Partner)
    serviceConnection(newValue) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newValue !== this.initialData.serviceConnection) {
          this.formResponse = JSON.parse(JSON.stringify(this.defaultSubFormTemplate));
        } else {
          this.formResponse = JSON.parse(JSON.stringify(this.initialData.subFormData));
        }
      }
    },
  },

  computed: {
    relationNameError() {
      if (this.v$.formResponse.relationName.$error && this.v$.formResponse.relationName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return NAME_SYMBOL_MESSAGE;
      }

      if (this.v$.formResponse.relationName.$error && this.v$.formResponse.relationName.maxLength.$invalid) {
        return MAX_CHARS_MESSAGE;
      }

      return 'Please include a response for name of connected individual.';
    },

    workPlaceError() {
      if (this.v$.formResponse.connectionName.$error && this.v$.formResponse.connectionName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return NAME_SYMBOL_MESSAGE;
      }

      if (this.v$.formResponse.connectionName.$error && this.v$.formResponse.connectionName.maxLength.$invalid) {
        return MAX_CHARS_MESSAGE;
      }

      return `Please include a response for ${this.formLabels.connectionInfo.connectionNameLabel}.`;
    },
  },

  mounted() {
    this.formResponse = JSON.parse(JSON.stringify(this.subFormData));
    this.updateFormFields();
  },

  validations: {
    formResponse: {
      connectionType: {
        required,
      },
      connectionName: {
        required,
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(480),
      },
      relationName: {
        required,
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(480),
      },
    },
  },

  methods: {
    ...mapMutations(['updateEligibilityFormData']),

    validationStatus(validation) {
      // TODO: find out how to modularise this instead of using it multiple times across child components
      return {
        error: validation.$error,
        dirty: validation.$dirty,
      };
    },
    showDeclaration() {
      this.$emit('showDeclaration', this.formResponse.connectionType);
    },

    updateFormFields() {
      if (this.formResponse.connectionType !== '') {
        this.showDeclaration();
      }
      this.formLabels.connectionInfo.connectionNameLabel = getEligibilityWorkLabel(this.formResponse.connectionType);
      this.formLabels.connectionInfo.connectionNamePlaceholder = getEligibilityWorkPlaceHolder(this.formResponse.connectionType);
    },

    returnWhiteSpaceIfEmptyStr,
  },
};
</script>
